import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../axiosInstance";
// The shared `api` instance already sets baseURL, withCredentials, and
// (if you kept the Bearer fallback) the Authorization header, so no
// thunk builds auth headers by hand any more.

/* ------------------------------------------------------------------ */
/* Helper: build query string from params object                      */
/* ------------------------------------------------------------------ */
function buildQueryString(basePath, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.append(key, String(value));
    }
  });
  const query = qs.toString();
  return query ? `${basePath}?${query}` : basePath;
}

/* ------------------------------------------------------------------ */
/* Async Thunks                                                       */
/* ------------------------------------------------------------------ */

export const getAnalyticsOverview = createAsyncThunk(
  "analytics/getOverview",
  async (params = { days: 30 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        buildQueryString(`/analytics/overview`, params),
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAnalyticsTopPages = createAsyncThunk(
  "analytics/getTopPages",
  async (params = { limit: 10, days: 30 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        buildQueryString(`/analytics/top-pages`, params),
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAnalyticsTrafficSources = createAsyncThunk(
  "analytics/getTrafficSources",
  async (params = { days: 30 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        buildQueryString(`/analytics/traffic-sources`, params),
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAnalyticsEcommerce = createAsyncThunk(
  "analytics/getEcommerce",
  async (params = { days: 30 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        buildQueryString(`/analytics/ecommerce`, params),
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAnalyticsCountries = createAsyncThunk(
  "analytics/getCountries",
  async (params = { days: 30 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        buildQueryString(`/analytics/countries`, params),
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAnalyticsTowns = createAsyncThunk(
  "analytics/getTowns",
  async (params = { days: 30 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        buildQueryString(`/analytics/towns`, params),
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAnalyticsRealtime = createAsyncThunk(
  "analytics/getRealtime",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/analytics/realtime`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAnalyticsFunnel = createAsyncThunk(
  "analytics/getFunnel",
  async (params = { days: 30 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        buildQueryString(`/analytics/funnel`, params),
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAnalyticsEvents = createAsyncThunk(
  "analytics/getEvents",
  async (params = { days: 30 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        buildQueryString(`/analytics/events`, params),
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

/* ------------------------------------------------------------------ */
/* Slice                                                              */
/* ------------------------------------------------------------------ */

const initialState = {
  overview: {},
  topPages: [],
  trafficSources: [],
  ecommerce: [],
  realtime: { users: 0, data: [] },
  funnel: [],
  countries: [],
  towns: [],
  events: [],
  loading: false,
  error: null,
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    clearAnalyticsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---------- Overview ---------- */
      .addCase(getAnalyticsOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(getAnalyticsOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Top Pages ---------- */
      .addCase(getAnalyticsTopPages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsTopPages.fulfilled, (state, action) => {
        state.loading = false;
        state.topPages = action.payload;
      })
      .addCase(getAnalyticsTopPages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Traffic Sources ---------- */
      .addCase(getAnalyticsTrafficSources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsTrafficSources.fulfilled, (state, action) => {
        state.loading = false;
        state.trafficSources = action.payload;
      })
      .addCase(getAnalyticsTrafficSources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Ecommerce ---------- */
      .addCase(getAnalyticsEcommerce.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsEcommerce.fulfilled, (state, action) => {
        state.loading = false;
        state.ecommerce = action.payload;
      })
      .addCase(getAnalyticsEcommerce.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Funnel ---------- */
      .addCase(getAnalyticsFunnel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsFunnel.fulfilled, (state, action) => {
        state.loading = false;
        state.funnel = action.payload;
      })
      .addCase(getAnalyticsFunnel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Countries ---------- */
      .addCase(getAnalyticsCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload;
      })
      .addCase(getAnalyticsCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Towns ---------- */
      .addCase(getAnalyticsTowns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsTowns.fulfilled, (state, action) => {
        state.loading = false;
        state.towns = action.payload;
      })
      .addCase(getAnalyticsTowns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Realtime ---------- */
      .addCase(getAnalyticsRealtime.fulfilled, (state, action) => {
        state.loading = false;
        state.realtime = {
          users: action.payload.users || 0,
          data: action.payload.data || [],
        };
      })

      /* ---------- Events ---------- */
      .addCase(getAnalyticsEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload;
      })
      .addCase(getAnalyticsEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAnalyticsError } = analyticsSlice.actions;
export default analyticsSlice.reducer;